import { Injectable, inject } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Network } from '@capacitor/network';
import BackgroundGeolocation, {
  AuthorizationStatus,
  Config,
  CurrentPositionRequest,
  Location,
  State,
} from '@transistorsoft/capacitor-background-geolocation';

import {
  AsyncSubject,
  Observable,
  firstValueFrom,
  interval,
  switchMap,
  Subscription,
  map,
  catchError,
  of,
} from 'rxjs';
import { TrackJS } from 'trackjs';

import { environment } from '../../environments/environment';

import { AppData } from '../data';
import {
  Job,
  _ScheduledFlagStop,
  Coordinate,
  Device,
  DeviceContext,
  GenericRouteEvent,
  GpsReport,
  JobType,
  OperatorContext,
  Route,
} from '../types';
import { MapTypeEnum, NativePluginService } from './plugin';
import {
  AlertService,
  GoogleApiService,
  LocationUtilityService,
  MetersPerSecToMilesPerHrMultiplier,
  MilesToMetersMultiplier,
  NotificationService,
  TranslateService,
} from '../services';
import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';


@Injectable({
  providedIn: 'root',
})
export class LocationService {
  private pluginSvc = inject(NativePluginService);
  private googleSvc = inject(GoogleApiService);
  private data = inject(AppData);
  private platform = inject(Platform);
  private alertSvc = inject(AlertService);
  private translateSvc = inject(TranslateService);
  private notificationSvc = inject(NotificationService);

  /*
   * Async subjects act kind of like promises in that they will only
   * emit a value once complete() has been called. Since the initialize
   * is a promise, we can create a subject we listen to  in order to wait
   * until initialization is done before other work is performed.
   */
  private initialized = new AsyncSubject<boolean>();
  private readonly gtm = (<any>window).dataLayer || [];

  private webGeoCheck: Subscription | null;
  private lastKnownLocations = [];
  private retryOnLocationListener = 0;

  /**
   * Returns true when the location service has been initialized,
   * if the initialization fails, will return false.
   */
  get initialized$(): Promise<boolean> {
    return firstValueFrom(this.initialized.asObservable());
  }

  mockLocation: boolean = false;
  lastLocation: Location | null = null;

  private lastUserEnteredValue: { bbgo: number; user: number; adjustment: number } = {
    bbgo: 0,
    user: 0,
    adjustment: 0,
  };
  private lastBBGOOdometerValue: number = 0;

  private readonly blankGPSReport: GpsReport = {
    accuracy: 0,
    direction: 0,
    heading: 0,
    latitude: 0,
    longitude: 0,
    dateTimeReported: new Date(),
    occuranceDate: new Date(),
    speed: 0,
    odometer: 0,
    milesFromPrevious: 0,
    confidence: 0,
  };

  // a little messy, but keep in mind that this gets set from the server upon login
  public paraScopeGpsReportingInterval = 60000;

  // this is set when we navigate so that we can bring app to front when we arrive
  destination: Coordinate;

  appName = environment.appName;
  //  translate service is not correctly translating these, so for the time being hard setting to english
  //  TODO: handle spanish
  backgroundPermissionRationale = {
    title: "Allow {applicationName} to access this device's location in the background?",
    message: "To track your vehicle route and provide precise ETAs for dispatching while this app is closed or not in use, please enable \"{backgroundPermissionOptionLabel}\" location permission.",
    positiveAction: 'Change to {backgroundPermissionOptionLabel}',
    negativeAction: 'Cancel',
  };

  /** IOS */
  locationAuthorizationAlert = {
    titleWhenNotEnabled: "Allow {applicationName} to access this device's location in the background?",
    titleWhenOff: "Allow {applicationName} to access this device's location in the background?",
    title: "Allow {applicationName} to access this device's location in the background?",
    instructions: 'To keep your dispatch manifest synchronized and track your vehicle route while navigating, please change your Location settings to "Always Allow".',
    cancelButton: "Cancel",
    settingsButton: 'Settings',
  };

  private BBGEOConfig: Config = {
    reset: true,

    /**
     * Distance Filter
     * Default: 10
     * The minimum distance (measured in meters) a device must move horizontally before an update event is generated.
     * However, by default, distanceFilter is elastically auto-calculated by the plugin: When speed increases, distanceFilter increases;
     * when speed decreases, so too does distanceFilter.
     *
     * We might want to increased if customer complaint of drift when they are not moving
     */
    distanceFilter: 10,

    /**
     * DesiredOdometerAccuracy
     * Default: 100, Location accuracy threshold in meters for odometer calculations.
     * The maximum location accuracy allowed for a location to be used for Location.odometer calculations.
     * Defaults to 100. If a location arrives having accuracy > desiredOdometerAccuracy, that location will not be used to update the odometer.
     * If you only want to calculate odometer from GPS locations, you could set desiredOdometerAccuracy: 10.
     * This will prevent odometer updates when a device is moving around indoors, in a shopping mall, for example.
     *
     * May want to look at analytics to determine device specific settings
     */
    desiredOdometerAccuracy: 30,

    /**
     * allowIdenticalLocations
     * Default: false
     * The Android plugin will ignore a received location when it is identical to the last location.
     * Set true to override this behavior and record every location, regardless if it is identical to the last location.
     */
    allowIdenticalLocations: true,

    /**
     * logLevel
     * Controls the volume of recorded events in the plugin's logging database.
     * BackgroundGeolocation contains powerful logging features.
     * By default, the plugin boots with a value of BackgroundGeolocation.LOG_LEVEL_OFF,
     * storing logMaxDays (default 3) days worth of logs in its SQLite database.
     */
    logLevel: BackgroundGeolocation.LOG_LEVEL_ERROR,
    /**
     * maxRecordsToPersist
     * Maximum number of records to persist in plugin's SQLite database.
     * Default -1 means no limit. To disable persisting locations, set this to 0
     */
    maxRecordsToPersist: 0,

    /**
     * preventSuspend
     * [iOS only] Prevent iOS from suspending your application in the background after location-services have been switched off.
     * Defaults to false. Set true to prevent iOS from suspending your application after location-services have been switched off
     * while running in the background. Must be used in conjunction with a heartbeatInterval.
     */
    preventSuspend: true,
    /**
     * heartbeatInterval
     * Controls the rate (in seconds) the BackgroundGeolocation.onHeartbeat event will fire.
     * ⚠️ Warning:
     * On iOS the BackgroundGeolocation.onHeartbeat event will fire only when configured with preventSuspend true.
     * Android minimum interval is 60 seconds. It is impossible to have a heartbeatInterval faster than this on Android.
     */
    heartbeatInterval: 60,

    /**
     * 🧪 speedJumpFilter
     * Android-only Experimental filter to ignore anomalous locations that suddenly jump an unusual distance from last.
     * The SDK will calculate an apparent speed and distance relative to last known location.
     * If the location suddenly teleports from last location, it will be ignored.
     * The measurement is in meters/second.
     * The default is to throw away any location which apparently moved at 300 meters/second from last known location.
     *
     * This may be something we may want to adjust
     */
    speedJumpFilter: 40, // Default: 300

    /**
     * ==========================================
     * 🚀 ANDROID 11+ RATIONALE CONFIG (CORRECT FOR V8)
     * ==========================================
     */
    // Mandates that the plugin requests the "Always Allow" background state
    locationAuthorizationRequest: 'Always',
    backgroundPermissionRationale: this.backgroundPermissionRationale,

    /**
     * ==========================================
     * 🍎 IOS AUTHORIZATION ALERT
     * ==========================================
     */
    locationAuthorizationAlert: this.locationAuthorizationAlert,

    disableStopDetection: true,
    pausesLocationUpdatesAutomatically: false,

    // This automatically sets up your compliant Foreground Service Notification:
    foregroundService: true,
    stopOnTerminate: false,
    startOnBoot: true,
    notification: {
      // Explicit text proving to Google that the driver sees exactly what is running
      //  TODO: translate
      title: `${this.appName} Tracking Active`,
      text: `Tracking vehicle location and motion activity for dispatch telemetry.`,
      channelName: 'Driver Location & Motion Tracking v4',
      priority: BackgroundGeolocation.NOTIFICATION_PRIORITY_DEFAULT,
      smallIcon: 'drawable/ic_stat_notification',
    },
    isMoving: true,
  };

  /**
   * @param {HTMLIonLoadingElement} [loading] - Pass-through reference to update screen overlays.
   * @returns {Promise<boolean>} Whether the telemetry service successfully spun up.
   */
  public async init(loading?: HTMLIonLoadingElement): Promise<boolean> {
    try {
      // Web / Non-Native Browser Fallback Lifecycle
      if (!this.pluginSvc.isNativePlatform) {
        const report = await this.getGPSReport();
        this.data.set('gpsReport', report);
        if (!this.webGeoCheck) {
          this.webGeoCheck = this.listenForWeb().subscribe();
        }
        this.initialized.next(true);
        return true;
      }

      // 2. Core Native Device Platform Handshake Verification
      await this.platform.ready();
      await BackgroundGeolocation.setConfig(this.BBGEOConfig);
      // Initialize state for non-Android platforms (iOS initialization boundary)
      let state: State = await BackgroundGeolocation.ready(this.BBGEOConfig);

      // retrieve the current location services authorization and hardware state
      let status: AuthorizationStatus = await BackgroundGeolocation.getProviderState()
        .then(value => value?.status ?? null)
        .catch(err => null);

      if (loading) loading.message = `Background Location Status: ${status}`;

      // Request permission if not already determined or if when-in-use needs upgrading
      switch (status) {
        case null:
        case BackgroundGeolocation.AUTHORIZATION_STATUS_NOT_DETERMINED:
        case BackgroundGeolocation.AUTHORIZATION_STATUS_WHEN_IN_USE:
        case BackgroundGeolocation.AUTHORIZATION_STATUS_RESTRICTED:
        case BackgroundGeolocation.AUTHORIZATION_STATUS_DENIED:
          status = await BackgroundGeolocation.requestPermission();
          break;
      }

      // get clean state
      status = await BackgroundGeolocation.getProviderState()
        .then(value => {
          return value.status
        })
        .catch(err => null);

      if (status !== BackgroundGeolocation.AUTHORIZATION_STATUS_ALWAYS) {
        this.initialized.next(false);
        return;
      }

      console.log('isANdroid', Capacitor.getPlatform() === 'android')
      if (Capacitor.getPlatform() === 'android') {
        // Directs the user straight to the system battery optimization settings screen
        await BackgroundGeolocation.deviceSettings.isIgnoringBatteryOptimizations()
          .then(isIgnoring => { if (!isIgnoring) { return BackgroundGeolocation.deviceSettings.showIgnoreBatteryOptimizations(); } })
          .catch(err => { console.error('error ignoring battery optimization', err) })

        let hasNotificationsPerm = await LocalNotifications.checkPermissions()?.then(status => status.display === 'granted')
        if (!hasNotificationsPerm && Capacitor.getPlatform() === 'android') {
          await LocalNotifications.requestPermissions()
            .catch(err => console.error('error requesting notification permissions', err));
        }
        hasNotificationsPerm = await LocalNotifications.checkPermissions()?.then(status => status.display === 'granted')
        if (!hasNotificationsPerm) {
          alert("Notification permission is required to display active route tracking.");
        }

        }


      // Re-register telemetry record listeners cleanly now that permissions match
      await this.resetListeners();

      // Native Hardware & Sensor Handshake
      const sensors = await BackgroundGeolocation.getSensors();
      if (loading) loading.message = `Found Gyroscope: ${sensors.gyroscope}`;
      if (environment.trackJS?.token) {
        TrackJS.addMetadata('hasGyroscope', sensors.gyroscope ? 'true' : 'false');
      }
      this.gtm.push({ gyroscope: sensors.gyroscope });

      // Start the core recording engine thread if it isn't ticking yet
      if (!state.enabled) {
        if (loading) loading.message = `Starting location change listener...`;
        state = await BackgroundGeolocation.start().catch(err => null);
      }

      // Establish Baseline Telemetry Report
      if (loading) loading.message = `Getting Current Location...`;
      const report = await this.getGPSReport();
      if (loading) loading.message = `Initial GPS Report Generated`;
      console.debug('Initial GPS Report Generated:', report);
      this.data.set('gpsReport', report);

      if (!state.isMoving) {
        if (loading) loading.message = `Setting Cadence...`;
        // Direct native validation of changePace cadence assignment
        await BackgroundGeolocation.changePace(true).catch(() => null);
      }

      if (loading) loading.message = `Location Init: ${state.enabled}`;

      // ✅ Mark initialization as fully completed and successful
      this.initialized.next(true);
      return state.enabled;
    } catch (err) {
      console.error('---------------> Core Location Service Initialization Failure:', err);
      this.initialized.next(false);
      return false;
    } finally {
      // ✅ Unblock downstream awaited code paths under all circumstances
      this.initialized.complete();
    }
  }

  convertMetersToMiles(meters: number): number {
    return Math.round(meters / MilesToMetersMultiplier);
  }

  async onLocation(location: Location) {
    // TrackJS.console.log('onLocation Fn');
    try {
      if (!location) {
        TrackJS.track('No location');
        return;
      }
      this.lastLocation = location || <Location>{ activity: {}, coords: {} };
      const {
        activity: { type, confidence },
        coords: { accuracy, latitude, longitude },
        odometer,
        timestamp,
      } = location;

      let {
        coords: { speed, heading },
      } = location;

      speed =
        (speed || 0) >= 0
          ? Math.min(999, 999, Math.round((speed || 0) * MetersPerSecToMilesPerHrMultiplier))
          : 0;

      const lastUserEnteredValue = this.lastUserEnteredValue?.user ?? 0;
      this.lastBBGOOdometerValue = this.convertMetersToMiles(odometer);
      const diff =
        this.lastBBGOOdometerValue - (this.lastUserEnteredValue?.bbgo ?? lastUserEnteredValue);
      const newOdometerValue = lastUserEnteredValue + diff;

      heading = heading >= 0 ? Math.round(heading) : null;
      const date = new Date();

      const gpsReport = {
        accuracy,
        direction: 100,
        heading,
        latitude,
        longitude,
        dateTimeReported: date,
        occuranceDate: new Date(timestamp),
        speed,
        odometer: newOdometerValue,
        milesFromPrevious: Math.abs((this.data.gpsReport.odometer || 0) - newOdometerValue),
      };
      this.data.set('gpsReport', gpsReport);
    } catch (err) {
      TrackJS.track(err);
    }
  }

  async checkPermissions(): Promise<AuthorizationStatus | void> {
    if (!this.pluginSvc.isNativePlatform) {
      return Promise.resolve();
    }
    return BackgroundGeolocation.requestPermission();
  }

  async stop(): Promise<State | void> {
    console.warn('stopping');
    await this.initialized$;
    if (!this.pluginSvc.isNativePlatform) return Promise.resolve();
    console.debug('cleaning up background listeners')
    await BackgroundGeolocation.stop();
    await BackgroundGeolocation.removeListeners(
      () => console.info('Removed background location listeners'),
      () => console.warn('Failed to remove background location listeners'),
    );
  }

  public async changePace(pace: boolean) {
    await this.initialized$;
    if (!this.pluginSvc.isNativePlatform) {
      return;
    }
    await BackgroundGeolocation.changePace(pace);
    console.warn('[BackgroundGeolocation] pace moving: ' + pace);
    return;
  }

  private async getGPSReport() {
    let gpsReport!: GpsReport;
    if (this.pluginSvc.isNativePlatform) {
      gpsReport = await this.getNativeReport();
    } else if (this.mockLocation) {
      gpsReport = await this.getGPSReportMock();
    } else {
      gpsReport = await this.getGPSReportWeb();
    }
    return gpsReport || ({} as GpsReport);
  }

  async getNativeReport(): Promise<GpsReport> {
    let currentPosition: Location;
    const _locations: Location[] = await BackgroundGeolocation.getLocations()
      .then(value => <Location[]>value ?? <Location[]>[])
      .catch(() => <Location[]>[]);
    if (_locations.length > 0) currentPosition = _locations[_locations.length - 1];
    const options: CurrentPositionRequest = {
      timeout: 5, // 30 second timeout to fetch location
      maximumAge: 20000, // Accept the last-known-location if not older than 5000 ms.
      desiredAccuracy: 30,
      samples: 1,
    };
    currentPosition =
      currentPosition ??
      (await BackgroundGeolocation.getCurrentPosition(options).catch(err => null));
    this.lastLocation = currentPosition;
    const {
      activity: { type, confidence },
      coords: { accuracy, latitude, longitude },
    } = currentPosition;

    let {
      coords: { speed, heading },
      odometer,
    } = currentPosition;

    speed =
      type == 'still' && confidence >= 75
        ? 0
        : Math.round(!isNaN(speed) && speed >= 0 ? speed * MetersPerSecToMilesPerHrMultiplier : 0);

    odometer = Math.round(odometer / MilesToMetersMultiplier);

    heading = heading >= 0 ? Math.round(heading) : null;
    const date = new Date();

    return {
      accuracy,
      direction: 100,
      heading,
      latitude,
      longitude,
      dateTimeReported: date,
      occuranceDate: date,
      speed,
      odometer,
      milesFromPrevious: 0,
      confidence,
    };
  }

  private listenForWeb(): Observable<GpsReport> {
    return interval(30_000).pipe(
      switchMap(() => this.getGPSReportWeb()),
      map(({ latitude, longitude, odometer, ...params }) => ({
        ...params,
        //  simulating car movement
        latitude: latitude + 0.01,
        longitude: longitude + 0.01,
        odometer: odometer + 1,
      })),
      catchError(error => of(this.blankGPSReport)),
    );
  }

  private async getWebPosition(): Promise<GeolocationPosition> {
    if (!navigator?.geolocation?.getCurrentPosition) {
      console.warn("don't have access to navigator or browser does not seem to support navigator.geolocation.getCurrentPosition");
      throw new Error("can't find navigator.geolocation.getCurrentPosition");
    }
    return new Promise(resolve => {
      //  allow service to return an old report that is up to 30 seconds old
      //  default timeout = 0 which means that it can never return a cached location
      const position = {
        coords: {
          latitude: 39.833333,
          longitude: -98.583333,
          accuracy: 0,
          heading: 0,
          altitude: 0,
          altitudeAccuracy: 0,
          speed: 0,
        },
        timestamp: new Date().valueOf(),
      };
      try {
        navigator.geolocation.getCurrentPosition(
          position => resolve(position),
          error => {
            console.debug(error);
            return position;
          },
          { timeout: 5000, enableHighAccuracy: true },
        );
      } catch (err) {
        console.warn(err);
      } finally {
        resolve(<GeolocationPosition>position);
      }
    });
  }

  private async getGPSReportWeb(): Promise<GpsReport> {
    try {
      const date = new Date();

      const location: GeolocationPosition = await this.getWebPosition();
      const { latitude, longitude, accuracy, heading, speed } = location?.coords || {};

      return {
        latitude,
        longitude,
        accuracy,
        direction: 100,
        dateTimeReported: date,
        occuranceDate: location?.timestamp ? new Date(location.timestamp) : date,
        speed: speed || 0,
        odometer: this.data.gpsReport.odometer,
        milesFromPrevious: 0,
        heading: heading || 100,
        confidence: 0,
      };
    } catch (error) {
      console.error(error);
      return Promise.resolve(this.blankGPSReport);
    }
  }

  private async getGPSReportMock(): Promise<GpsReport> {
    try {
      return {
        accuracy: 0,
        direction: 100,
        heading: 0,
        latitude: 0,
        longitude: 0,
        dateTimeReported: new Date(),
        occuranceDate: new Date(),
        speed: 0,
        odometer: 0,
        milesFromPrevious: 0,
        confidence: 0,
      };
    } catch (error) {
      console.error(error);
      return this.blankGPSReport;
    }
  }

  hasArrived(destination: Coordinate): boolean {
    if (!destination) {
      return false;
    }
    const gpsReport = this.data.gpsReport;
    const euclideanDistanceInMiles = LocationUtilityService.euclideanDistanceInMiles(
      gpsReport.latitude,
      gpsReport.longitude,
      destination.latitude,
      destination.longitude,
    );
    if (euclideanDistanceInMiles < 0.5) {
      return true;
    }
    return false;
  }

  /** Location Alert if driver location too far from Job location */
  public async checkHasArrived(routeEvents: GenericRouteEvent[]): Promise<boolean> {
    console.log('check arrive');
    const tooFar = routeEvents.filter(re => !this.hasArrived(re.location?.coordinate));
    if (!tooFar?.length) {
      return true;
    }

    let message = this.translateSvc.translate('LABEL.jobNotInRange') + '\n';
    tooFar.forEach(re => {
      if (re instanceof Job) {
        message =
          message +
          `\n` +
          `• ${
            re.value.jobType === JobType.Pickup
              ? this.translateSvc.translate('LABEL.pickup') + ' '
              : this.translateSvc.translate('ACTION.dropOff') + ' '
          } ${this.translateSvc.translate('LABEL.for')} ` +
          `${re.value.riderFirstName}  ${re.value.riderLastName}`;
      }
      if (re instanceof _ScheduledFlagStop) {
        message =
          message +
          `\n` +
          `• ${this.translateSvc.translate('LABEL.scheduledStop')} ${this.translateSvc.translate('LABEL.at')} ` +
          `${typeof re.value.location === 'string' ? re.value.location : re.value.location?.name || ''}`;
      }
    });

    return new Promise(resolve => {
      this.alertSvc.presentAlert({
        cssClass: 'alert-wrapperz',
        header: this.translateSvc.translate('LABEL.confirmArrival'),
        backdropDismiss: false,
        message,
        buttons: [
          {
            text: this.translateSvc.translate('LABEL.yes'),
            cssClass: 'primary ',
            handler: async () => {
              resolve(true);
            },
          },
          {
            text: this.translateSvc.translate('LABEL.no'),
            cssClass: 'danger ',
            handler: () => {
              resolve(false);
            },
          },
        ],
      });
    });
  }

  /**
   * This should only be called when user is adjusting the odometer manually to adjust for error.
   * @param odometer odom is in miles and needs to be converted to meters.
   */
  async setOdometer(odometer: number): Promise<void> {
    await this.initialized$;
    const report = this.data.gpsReport;
    this.lastUserEnteredValue = {
      user: odometer,
      bbgo: this.lastBBGOOdometerValue,
      adjustment: odometer - this.lastBBGOOdometerValue,
    };
    this.data.set('gpsReport', { ...report, odometer });
  }

  milesToMeters(miles: number): number {
    return miles * MilesToMetersMultiplier;
  }

  /**
   * Opens Google Maps and initializes it in navigate mode for a given location.
   * @param Coordinate - the location coordinates to navigate to.
   */
  async navigateToDestination(coordinate: Coordinate | null): Promise<any> {
    if (!coordinate) {
      console.debug('no coordinates found');
      return null;
    }
    try {
      await this.initialized$;
      this.destination = coordinate;
      return this.pluginSvc.launchMappingApplication(
        coordinate.latitude,
        coordinate.longitude,
        MapTypeEnum.TurnByTurnDirections,
      );
    } catch (err) {
      console.error(err);
      return this.notificationSvc.error();
    }
  }

  /**
   * Calculate the arrival time using either the google calculate eta service or a goofy estimate algorithm.
   * @param lat2 number - end location latitude
   * @param lng2 number - end location longitude
   * @param fromDate Date - the date to offset from.
   */
  async calculateArrivalTime(
    lat2: number,
    lng2: number,
    fromDate: Date = null,
  ): Promise<Date | null> {
    try {
      await this.initialized$;
      const gpsReport: GpsReport = this.data.gpsReport;
      const eta: Date = await this.googleSvc.calculateETA(
        gpsReport.latitude,
        gpsReport.longitude,
        lat2,
        lng2,
        fromDate,
      );
      return eta;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  estimatedTimeOfArrival({
    latitude,
    longitude,
    date,
  }: {
    latitude: number;
    longitude: number;
    date: Date;
  }): Promise<Date | null> {
    if (!environment.features.enableGoogleMapsApiCall) {
      return null;
    }
    if (!latitude || !longitude) {
      return null;
    }
    return this.calculateArrivalTime(latitude, longitude, date);
  }

  /**
   * This opens Google Maps and shows a location.
   * @param coordinate Coordinate - the location coordinates to view on the map.
   * @param name string - the location name
   */
  async previewLocation(coordinate: Coordinate, name: string): Promise<any> {
    try {
      if (!coordinate) {
        return Promise.resolve();
      }
      return this.pluginSvc.launchMappingApplication(
        coordinate.latitude,
        coordinate.longitude,
        MapTypeEnum.Geolocation,
      );
    } catch (error) {
      console.error(error);
      return Promise.resolve();
    }
  }

  async toggleDebug(): Promise<State> {
    await this.initialized$;
    const state = await BackgroundGeolocation.getState();
    return BackgroundGeolocation.setConfig({ debug: !state.debug });
  }

  /**
   * @returns boolean success of the log
   */
  async emailLog(): Promise<boolean> {
    try {
      const Logger = BackgroundGeolocation.logger;
      // TODO: grab email from
      const success = await Logger.emailLog(environment.developerEmail);
      return success;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async logToTrackJS(original: GpsReport, sent: GpsReport) {
    if (!environment.trackJS?.token) {
      return;
    }
    try {
      //  Network Status
      await Network.getStatus()
        .then(value => {
          TrackJS.console.log(value);
          return value;
        })
        .catch(err => TrackJS.console.warn('networkStatus', err));

      //  Time Diff -- for staleness
      const timeDiff = sent?.dateTimeReported?.valueOf() - original?.dateTimeReported?.valueOf();
      TrackJS.console.log('timeDiff', timeDiff);

      //  Sensors
      await BackgroundGeolocation.getSensors()
        .then(value => TrackJS.console.log('sensors', value))
        .catch(err => TrackJS.console.warn('sensors', err));

      //  GPS
      const currentGPSFromBBGO = await this.getGPSReport()
        .then(value => {
          TrackJS.console.log('currentGPSFromBBGO', value);
          return value;
        })
        .catch(err => {
          TrackJS.console.warn('currentGpsFromBBGO', err);
          return null;
        });

      let distanceInMiles = 0;
      if (
        currentGPSFromBBGO?.latitude &&
        currentGPSFromBBGO?.longitude &&
        original?.latitude &&
        original?.longitude
      ) {
        distanceInMiles = LocationUtilityService.euclideanDistanceInMiles(
          original.latitude,
          original.longitude,
          currentGPSFromBBGO.latitude,
          currentGPSFromBBGO.longitude,
        );
        TrackJS.console.log('distanceInMiles', distanceInMiles);
        //  105.6 feet
        //  if the diff between the 2 time values is more than 30 seconds and distance >= 105.6ft log stale gps.
        if (!isNaN(timeDiff) && timeDiff > 3000 && distanceInMiles >= 0.02) {
          TrackJS.track('GPS is Stale');
        }
      }

      const odoDiff = original && sent ? sent.odometer - original.odometer : Infinity;
      let fiveAgoDiff = Infinity;
      //  5 records = 2.5 minutes worth of logs
      if (this.lastKnownLocations.length >= 5) {
        fiveAgoDiff = sent.odometer - this.lastKnownLocations[0].odometer;
      }
      if (
        Number(distanceInMiles.toFixed(0)) - odoDiff > 0 ||
        (sent.speed > 45 && odoDiff === 0) ||
        // Odo says they have gone less than 1.5 miles in 2.5 minutes when
        // BBGO has clocked a speed >= 30 within that time frame
        // 30 miles / hour = .5 miles / minute = 1.25
        ([...this.lastKnownLocations, sent].some(({ speed }) => speed >= 30) && fiveAgoDiff < 1.25)
      ) {
        TrackJS.track('ODO is Stale');
      }

      //  only keeping 5 in memory
      if (this.lastKnownLocations.length >= 5) {
        this.lastKnownLocations.shift();
      }
      if (!!sent) this.lastKnownLocations.push(sent);
    } catch (err) {
      TrackJS.console.warn(err);
      TrackJS.track('GPS and/or ODO check error');
    }
  }

  getAvlReport({
    gpsReport,
    customerId,
    route,
    device,
  }: {
    gpsReport: GpsReport;
    customerId: number;
    route: Route;
    device: Device;
  }): { location: Location; device: DeviceContext; operator: OperatorContext } {
    const {
      platform,
      modelVersion: version,
      identity: { uuid },
      cordovaVersion: cordova,
      modelVersion: model,
      manufacturer,
    } = device || { identity: {} };
    const { vehicleId, id: routeId, driverId } = route || {};
    const { odometer } = gpsReport;

    return {
      location: {
        age: Date.now() - (new Date(this.lastLocation?.timestamp) || new Date()).valueOf(),
        timestamp: new Date().toISOString(),
        coords: {
          altitude: this.lastLocation?.coords.altitude || 0,
          floor: this.lastLocation?.coords.floor || 0,
          altitude_accuracy: this.lastLocation?.coords.altitude_accuracy || 0,
          ...gpsReport,
        },
        battery: this.lastLocation?.battery || { level: 0, is_charging: false },
        activity: this.lastLocation?.activity || { confidence: 0, type: null },
        odometer,
        event: this.lastLocation?.event || '',
        is_moving: !!this.lastLocation?.is_moving,
        mock: false,
        uuid,
      } as Location,
      device: <DeviceContext>{ platform: platform, version, uuid, cordova, model, manufacturer },
      operator: <OperatorContext>{ customerId, driverId, vehicleId, routeId },
    };
  }

  private async resetListeners() {
    try {
      await BackgroundGeolocation.removeListeners(
        () => console.info('Removed background location listeners'),
        () => console.warn('Failed to remove background location listeners'),
      );
    } catch (error) {
      console.error(error);
    }

    /** Setup location listener */
    BackgroundGeolocation.onLocation(
      async location => {
        await this.onLocation(location);
        this.retryOnLocationListener = 0;
      },
      async error => {
        TrackJS.console.log('info', 'BackgroundGeolocation.onLocation error');
        TrackJS.track(error);
        if (this.retryOnLocationListener > 3) {
          TrackJS.track('onLocation retry > 3, reloading the app');
          window.location.reload();
          return;
        }
        await this.resetListeners();
        this.retryOnLocationListener += 1;
      },
    );
  }

  public async restartBBGO() {
    await BackgroundGeolocation.stop();
    await BackgroundGeolocation.start();
    await this.resetListeners();
    BackgroundGeolocation.changePace(
      true,
      () => {},
      err => console.error(err),
    );
  }

  public showLocationRequiredAlert() {
    return this.alertSvc.presentAlert({
      header: this.translateSvc.translate('MESSAGE.locationAuthorizationAlert.title').replace('{applicationName}', environment.appName),
      message: this.translateSvc.translate('MESSAGE.locationAuthorizationAlert.instructions'),
      buttons: [
        {
          text: this.translateSvc.translate('ACTION.ok'),
          cssClass: 'primary',
        },
      ],
    });
  }
}
